import { z } from "zod";

import type { SupersededProposalReceiptData } from "@/lib/actionRequests/proposalReceipt";
import {
  EXPERIMENT_ENTITY_PREFIX,
  isValidPrefixedKsuid,
} from "@/utils/ksuidGenerator";

const ASSUMPTION_ENTITY_PREFIX = "asm_";

const prefixedKsuid = (prefix: string, label: string) =>
  z.string().refine((id) => isValidPrefixedKsuid(id, prefix), {
    message: `Invalid ${label} public ID`,
  });

export const LearningConfidenceLevelSchema = z.enum([
  "NOT_CONFIDENT",
  "SOMEWHAT_CONFIDENT",
  "CONFIDENT",
  "VERY_CONFIDENT",
]);

export const LearningQaVerdictSchema = z.enum(["PERSEVERE", "PIVOT", "KILL"]);

export const LearningTitleSchema = z.string().trim().min(1).max(255);

export const AssumptionDeltaSchema = z
  .object({
    sourceAssumptionPublicId: prefixedKsuid(ASSUMPTION_ENTITY_PREFIX, "assumption"),
    from: z.string().min(1).max(2000),
    to: z.string().min(1).max(2000),
  })
  .strict();

/** The pre-migration card/result contract did not cap assumption copy. */
export const LegacyAssumptionDeltaSchema = z.object({
  sourceAssumptionPublicId: prefixedKsuid(ASSUMPTION_ENTITY_PREFIX, "assumption"),
  from: z.string().min(1),
  to: z.string().min(1),
});

export const LearningQaPreDraftSchema = z
  .object({
    title: LearningTitleSchema,
    narrative: z.string().min(1).max(5000),
    assumptionDelta: AssumptionDeltaSchema,
    confidenceLevel: LearningConfidenceLevelSchema,
    verdict: LearningQaVerdictSchema,
  })
  .strict();

const LegacyLearningQaPreDraftSchema = z.object({
  title: LearningTitleSchema,
  narrative: z.string().min(1),
  assumptionDelta: LegacyAssumptionDeltaSchema,
  confidenceLevel: LearningConfidenceLevelSchema,
  verdict: LearningQaVerdictSchema,
});

export const LearningQaSourceAssumptionSchema = z
  .object({
    sourceAssumptionPublicId: prefixedKsuid(ASSUMPTION_ENTITY_PREFIX, "assumption"),
    statement: z.string().min(1).max(512),
  })
  .strict();

export const LearningDraftProposalBasisSchema = z
  .object({
    sourceAssumptions: z
      .array(
        z
          .object({
            sourceAssumptionPublicId: prefixedKsuid(
              ASSUMPTION_ENTITY_PREFIX,
              "assumption",
            ),
            revisionRef: z.string().regex(/^asr_[A-Za-z0-9_-]{22}$/),
          })
          .strict(),
      )
      .min(1)
      .max(25),
  })
  .strict();

const LegacyLearningQaSourceAssumptionSchema = z.object({
  sourceAssumptionPublicId: prefixedKsuid(ASSUMPTION_ENTITY_PREFIX, "assumption"),
  statement: z.string().min(1),
});

const addSourceAssumptionMembershipIssue = (
  payload: {
    sourceAssumptions?: z.infer<typeof LearningQaSourceAssumptionSchema>[];
    preDraft: z.infer<typeof LearningQaPreDraftSchema>;
  },
  context: z.RefinementCtx,
) => {
  if (
    payload.sourceAssumptions &&
    !payload.sourceAssumptions.some(
      (assumption) =>
        assumption.sourceAssumptionPublicId ===
        payload.preDraft.assumptionDelta.sourceAssumptionPublicId,
    )
  ) {
    context.addIssue({
      code: "custom",
      message: "Pre-draft source assumption is not selectable",
      path: ["preDraft", "assumptionDelta", "sourceAssumptionPublicId"],
    });
  }
};

export const LearningQaPayloadSchema = z
  .object({
    experimentPublicId: prefixedKsuid(EXPERIMENT_ENTITY_PREFIX, "experiment"),
    sourceAssumptions: z.array(LearningQaSourceAssumptionSchema).min(1).max(25),
    preDraft: LearningQaPreDraftSchema,
    proposalBasis: LearningDraftProposalBasisSchema,
  })
  .strict()
  .superRefine(addSourceAssumptionMembershipIssue);

/** Historical cards predate the server-projected current-path assumption list. */
const LegacyLearningQaPayloadSchema = z
  .object({
    experimentPublicId: prefixedKsuid(EXPERIMENT_ENTITY_PREFIX, "experiment"),
    sourceAssumptions: z
      .array(LegacyLearningQaSourceAssumptionSchema)
      .min(1)
      .optional(),
    preDraft: LegacyLearningQaPreDraftSchema,
  })
  .superRefine(addSourceAssumptionMembershipIssue);

export const LearningQaResolveSchema = z.object({
  title: LearningTitleSchema,
  narrative: z.string().min(1).max(5000),
  assumptionDelta: AssumptionDeltaSchema,
  confidenceLevel: LearningConfidenceLevelSchema,
  verdict: LearningQaVerdictSchema,
}).strict();

/** Display-only parser for persisted results written before copy-length caps were introduced. */
const LegacyLearningQaResolveSchema = LegacyLearningQaPreDraftSchema;

export type AssumptionDelta = z.infer<typeof AssumptionDeltaSchema>;
export type LearningQaPreDraft = z.infer<typeof LearningQaPreDraftSchema>;
export type LearningQaSourceAssumption = z.infer<typeof LearningQaSourceAssumptionSchema>;
export type LearningDraftProposalBasis = z.infer<typeof LearningDraftProposalBasisSchema>;
export type LearningQaPayload = z.infer<typeof LearningQaPayloadSchema>;
export type LearningQaResolve = z.infer<typeof LearningQaResolveSchema>;

export type LearningQaToolResult =
  | (LearningQaResolve & { toolCallId: string })
  | SupersededProposalReceiptData;

type LearningQaEntity = {
  type?: string;
  experimentPublicId?: string;
  sourceAssumptions?: LearningQaSourceAssumption[];
  preDraft?: LearningQaPreDraft;
  proposalBasis?: LearningDraftProposalBasis;
  toolInvoke?: { toolCallId?: string };
};

export type LearningQaArgs = {
  entities?: LearningQaEntity[];
  experimentPublicId?: string;
  sourceAssumptions?: LearningQaSourceAssumption[];
  preDraft?: LearningQaPreDraft;
  proposalBasis?: LearningDraftProposalBasis;
};

/**
 * Parses learning_qa card payload from assistant tool args (flat or entities[] shape).
 */
export function parseLearningQaPayload(
  args: unknown,
  toolCallId?: string,
  allowHistoricalReceipt = false,
) {
  const direct = LearningQaPayloadSchema.safeParse(args);
  if (direct.success) {
    return direct;
  }
  if (allowHistoricalReceipt) {
    const historicalDirect = LegacyLearningQaPayloadSchema.safeParse(args);
    if (historicalDirect.success) {
      return historicalDirect;
    }
  }

  const root = args as LearningQaArgs | null | undefined;
  const entities = root?.entities ?? [];
  const entity =
    entities.find(
      (item) =>
        item.type === "learning_qa" &&
        (!toolCallId || item.toolInvoke?.toolCallId === toolCallId),
    ) ?? entities.find((item) => item.type === "learning_qa");

  if (!entity) {
    return direct;
  }

  const candidate = {
    experimentPublicId: entity.experimentPublicId ?? root?.experimentPublicId,
    sourceAssumptions: entity.sourceAssumptions ?? root?.sourceAssumptions,
    preDraft: entity.preDraft ?? root?.preDraft,
    proposalBasis: entity.proposalBasis ?? root?.proposalBasis,
  };
  const currentEntity = LearningQaPayloadSchema.safeParse(candidate);
  if (currentEntity.success || !allowHistoricalReceipt) {
    return currentEntity;
  }
  return LegacyLearningQaPayloadSchema.safeParse(candidate);
}

/**
 * Parses a resolved receipt without widening the strict schema used for new submissions.
 */
export function parseLearningQaResolvedReceipt(result: unknown) {
  const root = result as
    | {
        title?: unknown;
        narrative?: unknown;
        assumptionDelta?: unknown;
        confidenceLevel?: unknown;
        verdict?: unknown;
      }
    | null
    | undefined;
  const candidate = {
    title: root?.title,
    narrative: root?.narrative,
    assumptionDelta: root?.assumptionDelta,
    confidenceLevel: root?.confidenceLevel,
    verdict: root?.verdict,
  };
  const current = LearningQaResolveSchema.safeParse(candidate);
  return current.success ? current : LegacyLearningQaResolveSchema.safeParse(candidate);
}
